import { Dialog } from '@dsi/react-eds';
import ReactEDSWrapper from '../../../../utils/react-eds-wrapper';
import { RecordsType, ReportsType } from '../../Reports_View/shared/types/shared.types';
import styles from './ReportDialog.module.css';
import ReactDOM from 'react-dom';
import React, { useState } from 'react';

interface PropsObject {
  data: RecordsType;
  type: string;
  metadataFields: { attribute: string; title: string }[];
  impactAssessmentFields: { attribute: string; title: string }[];
  open: boolean;
  handleClose: () => void;
}

function ReportDialog(props: Readonly<PropsObject>) {
  const data = props.data;
  const metadataFields = props.metadataFields;
  const impactAssessmentFields = props.impactAssessmentFields;
  const isKnownLimitations = props.type === ReportsType.KNOWN_LIMITATIONS;
  const isCorrectedLimitations = props.type === ReportsType.CORRECTED_LIMITATIONS;
  const isKll = isKnownLimitations || isCorrectedLimitations;
  const isPlaybook = props.type === ReportsType.PLAYBOOK;
  const isTrouble = props.type === ReportsType.TROUBLE;
  const [nirExpanded, setNirExpanded] = useState(false);
  const [operationalImpactExpanded, setOperationalImpactExpanded] = useState<boolean | null>(null);

  const SUBSECTION_ORDER: { key: string; title: string }[] = [
    { key: 'summary_and_benefits', title: 'Summary and Benefits' },
    { key: 'capacity_and_performance', title: 'Capacity and Performance' },
    { key: 'operation', title: 'Operation' },
    { key: 'interfaces', title: 'Interfaces' },
    { key: 'hardware', title: 'Hardware' },
    { key: 'other_network_elements', title: 'Other Network Elements' },
    { key: 'description_of_correction', title: 'Description of Correction' },
    { key: 'backward_compatible_changes', title: 'Backward Compatible Changes' }
  ];

  const getSubsectionValue = (key: string): string => {
    if (!data.subsections) return '';
    return (data.subsections as Record<string, string | undefined>)[key] || '';
  };

  const findDataNested = (attribute: string) => {
    if (attribute.includes('.')) {
      const attrs = attribute.split('.');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let obj: any;
      attrs.forEach((a, i) => {
        if (i === 0) {
          obj = data;
        }
        if (Array.isArray(obj[a]) && obj[a]?.length > 0) {
          obj = obj[a][0];
        } else {
          obj = obj[a] || '';
        }
      });
      return obj;
    } else {
      return data[attribute];
    }
  };

  const isEmptyContent = (text: string): boolean => {
    if (!text) return true;
    return text.trim() === '';
  };

  const formatContent = (text: string): React.ReactNode => {
    if (!text) return null;
    const parts = text.split(/\[List\]|\[\/List\]/);
    return parts.map((part, index) => {
      const trimmed = part.trim();
      if (!trimmed) return null;
      const lines = trimmed.split('\n').map((l) => l.trim()).filter(Boolean);
      const isListBlock = lines.every((line) => line.startsWith('- '));
      if (isListBlock) {
        return (
          <ul key={index} className={styles.subtitle} style={{ margin: '0.5em 0', paddingLeft: '1.5em' }}>
            {lines.map((line, i) => (
              <li key={i}>{line.replace(/^- /, '')}</li>
            ))}
          </ul>
        );
      }
      return (
        <p key={index} className={styles.subtitle} style={{ whiteSpace: 'pre-line' }}>
          {trimmed}
        </p>
      );
    });
  };

  const getDialogTitle = () => {
    if (isKll) return `TR ${data.tr_id || ''}`;
    return data?.feature_name || data.correction_title;
  };

  const getMetadataSectionTitle = () => {
    if (isPlaybook) return 'Feature Metadata';
    return 'Correction Metadata';
  };

  const getDescriptionSectionTitle = () => {
    if (isPlaybook) return 'NIR';
    if (isKll) return 'Description and Correction';
    return 'NIR';
  };

  const getDescriptionContent = () => {
    if (isPlaybook) return data.subsections?.summary_and_benefits || '';
    if (isKll) {
      const parts: string[] = [];
      if (data.observation_summary?.length) {
        parts.push(data.observation_summary.join('\n'));
      }
      if (data.solution_summary?.length) {
        parts.push(data.solution_summary.join('\n'));
      }
      return parts.join('\n\n') || data.description || '';
    }
    return data.subsections?.description_of_correction || '';
  };

  const hasFieldValues = (fields: { attribute: string; title: string }[]): boolean => {
    return fields.some((field) => {
      const value = findDataNested(field.attribute);
      return value !== undefined && value !== null && value !== '';
    });
  };

  return ReactDOM.createPortal(
    <ReactEDSWrapper
      fullscreen
      className={'full-dialog-scrollable'}
      component={Dialog}
      onClose={props.handleClose}
      show={props.open}
      title={getDialogTitle()}
    >
      <div>
        {!isKll && (
          <div className={`row ${styles.separate}`}>
            <div className="">
              <span className={styles.subtitle}>{`Section ${data.section_number} - ${data.release}`}</span>
            </div>
            <div className="">
              <span className={styles.subtitle}>{data.short_description}</span>
            </div>
          </div>
        )}
        {hasFieldValues(metadataFields) && (
          <div className={`row ${styles.separate}`}>
            <div className={`lg-12 ${styles.separate}`}>
              <span className={styles.title}>{getMetadataSectionTitle()}</span>
            </div>
            {metadataFields.map((field) => (
              <div className="lg-2" key={field.attribute}>
                <label>{field.title} </label>
                <label>{findDataNested(field.attribute)} </label>
              </div>
            ))}
          </div>
        )}
        {impactAssessmentFields.some((field) => data[field.attribute]) && (
          <div className={`row ${styles.separate}`}>
            <div className={`lg-12 ${styles.separate}`}>
              <span className={styles.title}>Impact Assessment</span>
            </div>
            {impactAssessmentFields.filter((field) => data[field.attribute]).map((field) => (
              <div className="lg-2" key={field.attribute}>
                <label style={{ color: '#0069c2' }}>{field.title} </label>
                <label>{data[field.attribute]} </label>
              </div>
            ))}
          </div>
        )}
        {(isPlaybook || isTrouble) && !!(data.cm_impact?.length || data.new_alarms?.length || data.kpi_deviations?.length) && (() => {
          const allContent = [
            ...(data.cm_impact || []),
            ...(data.new_alarms || []),
            ...(data.kpi_deviations || [])
          ].join(' ');
          const wordCount = allContent.split(/\s+/).filter(Boolean).length;
          const shouldCollapse = wordCount > 200;
          const isExpanded = operationalImpactExpanded !== null ? operationalImpactExpanded : !shouldCollapse;

          return (
            <div className={`row ${styles.separate}`}>
              {shouldCollapse ? (
                <div
                  className={`lg-12 ${styles.separate}`}
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                  onClick={() => setOperationalImpactExpanded(!isExpanded)}
                >
                  <i className={`icon ${isExpanded ? 'icon-chevron-down' : 'icon-chevron-right'}`} style={{ marginRight: '8px' }} />
                  <span className={styles.title}>Operational Impact</span>
                </div>
              ) : (
                <div className={`lg-12 ${styles.separate}`}>
                  <span className={styles.title}>Operational Impact</span>
                </div>
              )}
              {isExpanded && (
                <>
                  {data.cm_impact && data.cm_impact.length > 0 && (
                    <div className="lg-12">
                      <p className={styles.subtitle} style={{ fontWeight: 'bold' }}>CM Impact</p>
                      {data.cm_impact.map((item, index) => (
                        <p className={styles.subtitle} key={index}>{item}</p>
                      ))}
                    </div>
                  )}
                  {data.new_alarms && data.new_alarms.length > 0 && (
                    <div className="lg-12">
                      <p className={styles.subtitle} style={{ fontWeight: 'bold' }}>New Alarms</p>
                      {data.new_alarms.map((item, index) => (
                        <p className={styles.subtitle} key={index}>{item}</p>
                      ))}
                    </div>
                  )}
                  {data.kpi_deviations && data.kpi_deviations.length > 0 && (
                    <div className="lg-12">
                      <p className={styles.subtitle} style={{ fontWeight: 'bold' }}>KPI Deviations</p>
                      {data.kpi_deviations.map((item, index) => (
                        <p className={styles.subtitle} key={index}>{item}</p>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })()}
        {isPlaybook && data.subsections && SUBSECTION_ORDER.some(({ key }) => !isEmptyContent(getSubsectionValue(key))) && (
          <div className={`row ${styles.separate}`}>
            <div
              className={`lg-12 ${styles.separate}`}
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
              onClick={() => setNirExpanded(!nirExpanded)}
            >
              <i className={`icon ${nirExpanded ? 'icon-chevron-down' : 'icon-chevron-right'}`} style={{ marginRight: '8px' }} />
              <span className={styles.title}>{getDescriptionSectionTitle()}</span>
            </div>
            {nirExpanded &&
              SUBSECTION_ORDER
                .filter(({ key }) => !isEmptyContent(getSubsectionValue(key)))
                .map(({ key, title }) => (
                  <div className="lg-12" key={key}>
                    <p className={styles.subtitle} style={{ fontWeight: 'bold' }}>{title}</p>
                    {formatContent(getSubsectionValue(key))}
                  </div>
                ))}
          </div>
        )}
        {isTrouble && data.subsections && SUBSECTION_ORDER.some(({ key }) => !isEmptyContent(getSubsectionValue(key))) && (
          <div className={`row ${styles.separate}`}>
            <div
              className={`lg-12 ${styles.separate}`}
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
              onClick={() => setNirExpanded(!nirExpanded)}
            >
              <i className={`icon ${nirExpanded ? 'icon-chevron-down' : 'icon-chevron-right'}`} style={{ marginRight: '8px' }} />
              <span className={styles.title}>{getDescriptionSectionTitle()}</span>
            </div>
            {nirExpanded &&
              SUBSECTION_ORDER
                .filter(({ key }) => !isEmptyContent(getSubsectionValue(key)))
                .map(({ key, title }) => (
                  <div className="lg-12" key={key}>
                    <p className={styles.subtitle} style={{ fontWeight: 'bold' }}>{title}</p>
                    {formatContent(getSubsectionValue(key))}
                  </div>
                ))}
          </div>
        )}
        {isKll && !isEmptyContent(getDescriptionContent()) && (
          <div className={`row ${styles.separate}`}>
            <div className={`lg-12 ${styles.separate}`}>
              <span className={styles.title}>{getDescriptionSectionTitle()}</span>
            </div>
            <div className="lg-12">
              {formatContent(getDescriptionContent())}
            </div>
          </div>
        )}
      </div>
    </ReactEDSWrapper>,
    document.body
  );
}
export default ReportDialog;
